import React from 'react';
import { useTranslation } from 'react-i18next';
import { useIphoneDetection } from '../../../hooks/useIphoneDetection';
import useScreenSize from '../../../hooks/useScreenSize';
import {
  ContactsInfoBlock,
  ContactsInfoItem,
  ContactsInfoItems,
  ContactsInfoItemValue,
  DeskContactsInfoItemTitle,
  ContactUsWrapper,
  MobContactsInfoBlockTitle,
  QrCodeInfoContainer,
  TurboAppDescription,
  TurboAppLinkedTitle,
  TurboAppLinksBlock,
  TurboAppQrCodeLinked,
  DeskImage,
  MobImage
} from './styled';
import { useClient } from '../../../hooks/useClient';

interface ContactUsProps {
  turboAppStoreLink: string;
  turboPlayMarketLink: string;
  telAdditional: string;
  telAdditionalUrl: string;
  email: string;
}

export const ContactUs: React.FC<ContactUsProps> = ({
  turboAppStoreLink,
  turboPlayMarketLink,
  telAdditional,
  telAdditionalUrl,
  email
}) => {
  const { isLarge } = useScreenSize();
  const isClient = useClient();
  const { t } = useTranslation();
  const isIphone = useIphoneDetection();

  const contactsInfoList = [
    {
      title: t('common.phone'),
      value: telAdditional,
      path: telAdditionalUrl,
      cypress: 'footer-link-phone'
    },
    {
      title: t('common.email'),
      value: email,
      path: `mailto:${email}`,
      cypress: 'footer-link-social'
    }
  ];

  const showOnMobile = !isClient || !isLarge;
  const showOnDesk = !isClient || isLarge;

  return (
    <ContactUsWrapper data-cy="contact-us-block">
      <ContactsInfoBlock>
        {showOnMobile && (
          <MobContactsInfoBlockTitle data-cy="contact-us-title">
            {t('common.contact_us')}
          </MobContactsInfoBlockTitle>
        )}
        <ContactsInfoItems data-cy="contact-us-items">
          {contactsInfoList.map(({ title, value, path, cypress }, index) => (
            <ContactsInfoItem key={index}>
              {showOnDesk && (
                <DeskContactsInfoItemTitle data-cy={`${cypress}-title`}>
                  {title}
                </DeskContactsInfoItemTitle>
              )}
              <ContactsInfoItemValue href={path} data-cy={`${cypress}-value`}>
                {value}
              </ContactsInfoItemValue>
            </ContactsInfoItem>
          ))}
        </ContactsInfoItems>
      </ContactsInfoBlock>
      <TurboAppLinksBlock>
        {showOnDesk && (
          <DeskImage
            data-cy="turbo-screenshot"
            width={112}
            height={115}
            src="/static/images/turbo_screen.png"
            alt=""
          />
        )}
        {showOnMobile && (
          <MobImage
            width={45}
            height={45}
            src="/static/images/turbo_app_logo.png"
            data-cy="turbo-logo"
            alt=""
          />
        )}
        <QrCodeInfoContainer data-cy="qr-container">
          <TurboAppQrCodeLinked
            href={isIphone ? turboAppStoreLink : turboPlayMarketLink}
            target="_blank"
            data-cy="qr-turbo-container"
          >
            {showOnDesk && (
              <DeskImage
                data-cy="qr-turbo"
                aria-label="Turbo QR Code"
                width={45}
                height={45}
                src="/static/images/qr_code_turbo.jpg"
                alt="Turbo QR Code"
              />
            )}
            <TurboAppLinkedTitle data-cy="qr-turbo-title">
              {t('common.turbo_app')}
            </TurboAppLinkedTitle>
          </TurboAppQrCodeLinked>
          <TurboAppDescription data-cy="qr-turbo-description">
            {t('common.turbo_slogan')}
          </TurboAppDescription>
        </QrCodeInfoContainer>
      </TurboAppLinksBlock>
    </ContactUsWrapper>
  );
};
